import React, { useEffect, useState } from 'react'
import SearchTextBox from 'srcReact/components/common2/SearchTextBox'
import styles from './styles.module.scss'
import Avatar from 'srcReact/components/common2/Avatar'
import { useTranslation } from 'react-i18next'
import useInfinitScroll from 'srcReact/helpers/hooks/useInfinitScroll'
import { useSelector } from 'react-redux'
import NotFound from '../NotFound'
import OrderByDropdown from '../OrderByDropdown'
import Icon from 'srcReact/components/icons/Icon'
import FilterTabs from '../FilterTabs'
import ErrorMessage from '../ErrorMessage'

export default function MembersComponent({
  scope,
  type = '',
  isHybridMixr = false,
  order,
  members,
  membersLoading,
  searchedMemberError,
  participants,
  lastMemberRef,
  selectedAttendingType = '',
  handleSearchMember,
  handleOrderByChange,
  handleAttendingTypeChange,
  handleMixrScopeChange,
  showUserModal
}) {
  const { t } = useTranslation()

  const {
    login: { user: loggedInUser },
    common: { isMobile, isNative, isTablet }
  } = useSelector((state) => state)

  const [isHybridMixrExpanded, setIsHybridMixrExpanded] = useState(false)
  const toggleHybridMixrScope = () => {
    handleMixrScopeChange('participants')
    setIsHybridMixrExpanded(!isHybridMixrExpanded)
  }
  const changeMixrScope = (newScope) => {
    if (newScope !== 'participants') {
      setIsHybridMixrExpanded(false)
    }
    handleMixrScopeChange(newScope)
  }

  useEffect(() => {
    if (isHybridMixr && scope === 'participants') {
      setIsHybridMixrExpanded(true)
    }
  }, [])

  return (
    <div className={styles.membersContainer}>
      <div className={styles.actions}>
        <div className={styles.filters}>
          <SearchTextBox
            placeholder={isMobile ? t('global.search') : t('formlabel.searchMemberPlaceholder')}
            handleSearch={handleSearchMember}
            minimumSearchChar={2}
            errorMessage={t('member-directory.search_char_error', { character_count: 1 })}
            cssClass={styles.search}
            inputCssClass={`${styles.searchInput} ${
              (scope === 'staff' || scope.includes('invited_')) && styles.searchInputLarge
            }`}
          />
        </div>
        {!scope.includes('invited_') && <OrderByDropdown selected={order} handleOrderByChange={handleOrderByChange} />}
      </div>
      {type === 'mixr' && (
        <FilterTabs
          isExpanded={isHybridMixr && isHybridMixrExpanded}
          className={styles.filtersContainer}
          tabs={[
            {
              display: t('mixrHome.members.inviteFilters.all'),
              selected: scope === 'all',
              info: participants && participants.invited ? participants.invited.all : '',
              show: true,
              onClick: () => changeMixrScope('all')
            },
            {
              display: t('mixrHome.members.inviteFilters.going'),
              selected: scope === 'participants',
              info: participants && participants.going ? participants.going.total : '',
              show: true,
              onClick: () => toggleHybridMixrScope()
            },
            {
              display: (
                <React.Fragment>
                  {!isMobile && !isNative && t('mixrHome.members.inviteFilters.going')}
                  <Icon name='inpersonmixr' styleModifier='inpersonIcon' />
                </React.Fragment>
              ),
              show: isHybridMixr && isHybridMixrExpanded,
              selected: scope === 'participants' && selectedAttendingType === 'in_person',
              info: participants && participants.going ? participants.going.in_person : '',
              isSubTab: true,
              onClick: () => handleAttendingTypeChange('in_person')
            },
            {
              display: (
                <React.Fragment>
                  {!isMobile && !isNative && t('mixrHome.members.inviteFilters.going')}
                  <Icon name='virtualmixr-icon' styleModifier='virtualIcon' />
                </React.Fragment>
              ),
              show: isHybridMixr && isHybridMixrExpanded,
              selected: scope === 'participants' && selectedAttendingType === 'virtual',
              info: participants && participants.going ? participants.going.virtual : '',
              isSubTab: true,
              onClick: () => handleAttendingTypeChange('virtual')
            },
            {
              display: t('mixrHome.members.inviteFilters.noReply'),
              selected: scope === 'invited_no_reply',
              info: participants && participants.invited ? participants.invited.no_reply : '',
              show: true,
              onClick: () => changeMixrScope('invited_no_reply')
            },
            {
              display: t('mixrHome.members.inviteFilters.notGoing'),
              selected: scope === 'not_going',
              info: participants && participants.invited ? participants.invited.not_going : '',
              show: true,
              onClick: () => changeMixrScope('not_going')
            }
          ]}
        />
      )}

      <div className={styles.members}>
        {!membersLoading && searchedMemberError ? (
          <ErrorMessage className={styles.error} text={searchedMemberError} center={true} />
        ) : !membersLoading && members && members.length === 0 ? (
          <NotFound title={t('global.no_results')} className={styles.notFoundWrapper} />
        ) : (
          members &&
          members.map((usr, index) => {
            const isLastMember = members.length === index + 1
            return (
              <Avatar
                userId={usr.id}
                firstname={usr.firstname}
                lastname={usr.lastname}
                commonInterestsCount={usr.interests_in_common && usr.interests_in_common.length}
                profilePicture={usr.profile_picture}
                lastMemberRef={isLastMember ? lastMemberRef : null}
                key={'member_' + usr.id}
                containerCSS={styles.member}
                userNameCSS={styles.userNameCSS}
                rounded
                onClickAction={() => showUserModal(usr, index)}
                showUserName
                showCommonInterests={type === 'group' && usr.id !== loggedInUser.id}
                showRole={true}
                mimeType={usr.mime_type}
                originalTag={usr.original_tag}
                isOriginal={usr.is_original}
                imgWrapperSize='large84'
                withExpertiseGroup={usr.with_expertise_group}
                role={
                  usr.role === 'organizer'
                    ? t('components.avatar.organizer')
                    : usr.role === 'co-organizer'
                    ? t('components.avatar.co-organizer')
                    : usr.role === 'participant'
                    ? t('mixrHome.members.inviteFilters.going')
                    : usr.role === 'not_going'
                    ? t('mixrHome.members.inviteFilters.notGoing')
                    : usr.role === 'invited'
                    ? t('mixrHome.members.inviteFilters.noReply')
                    : ''
                }
                roleContainerCSS={
                  usr.role === 'organizer' || usr.role === 'co-organizer'
                    ? styles.organzerRole
                    : usr.role === 'participant'
                    ? styles.participantRole
                    : styles.noRole
                }
                goingType={usr.attending_type}
              />
            )
          })
        )}
      </div>
    </div>
  )
}
